import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { formatErr } from "../lib/api";
import { GoogleLogo } from "@phosphor-icons/react";
import PasswordInput from "../components/PasswordInput";

/**
 * iter 65 — login screen.
 *
 * Public sign-up via "Register a new company" was removed because every
 * new tenant must now be invited (either by a Developer through the
 * dev console, or by an existing org admin via the public-invite flow
 * under `/accept-invite/:token`).
 *
 * Demo credential hint was also removed — production logins should
 * never see throwaway test accounts. If you need the local seeded
 * admin password, it's documented in /app/memory/test_credentials.md.
 */
export default function Login() {
  const { login, signInWithGoogle } = useAuth();
  // iter 73.2 — Remember-me: persist last-used email in localStorage. We
  // deliberately only persist the EMAIL (never the password / session
  // token) — the user must still re-enter their password on every visit.
  // Aligns with the PRD's "remember-me preserves the email field but the
  // user still must sign in" rule.
  const [form, setForm] = useState(() => {
    try {
      const remembered = localStorage.getItem("lb_remember_me") === "1";
      const lastEmail = remembered ? (localStorage.getItem("lb_last_email") || "") : "";
      return { email: lastEmail, password: "" };
    } catch { return { email: "", password: "" }; }
  });
  const [rememberMe, setRememberMe] = useState(() => {
    try { return localStorage.getItem("lb_remember_me") === "1"; }
    catch { return false; }
  });
  const [err, setErr] = useState("");
  // iter 73.4 — Idle-logout flash message. We DON'T clear sessionStorage
  // here (React.StrictMode mounts the component twice in dev; clearing
  // on first mount means the second mount's useState initializer reads
  // an empty string and the badge never renders). Instead we leave the
  // message in sessionStorage and clear it once the user actually engages
  // with the form (handled in `submit()` and via a ref on focus/typing).
  const [idleMsg, setIdleMsg] = useState(() => {
    try { return sessionStorage.getItem("idle_logout_msg") || ""; }
    catch { return ""; }
  });
  const clearIdleMsg = () => {
    if (idleMsg) {
      try { sessionStorage.removeItem("idle_logout_msg"); } catch { /* ignore */ }
      setIdleMsg("");
    }
  };
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    clearIdleMsg();
    setLoading(true);
    try {
      await login(form.email, form.password);
      // Persist email AFTER a successful login only (so a typo'd email
      // doesn't get stuck in the field next visit).
      try {
        if (rememberMe) {
          localStorage.setItem("lb_remember_me", "1");
          localStorage.setItem("lb_last_email", form.email);
        } else {
          localStorage.removeItem("lb_remember_me");
          localStorage.removeItem("lb_last_email");
        }
      } catch { /* storage blocked — ignore */ }
    } catch (e) { setErr(formatErr(e)); }
    setLoading(false);
  };

  const google = async () => {
    setErr("");
    try { await signInWithGoogle(); }
    catch (e) { setErr(formatErr(e)); }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[#0A0A0A] text-white login-bg" style={{ backgroundColor: "#0A0A0A", backgroundBlendMode: "screen", backgroundImage: "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)" }}>
        <div className="flex items-center gap-3">
          <img src="/logbook-logo.png" alt="LogBook Legal" className="h-12 w-auto" data-testid="login-logo" />
        </div>
        <div className="space-y-6 max-w-md">
          <div className="label-xs text-white/60">PROCESS SERVING · INTAKE · COMPLIANCE</div>
          <h1 className="heading text-5xl leading-[0.95]">Run process serving<br/>like infrastructure.</h1>
          <p className="text-white/70 leading-relaxed">A control-room SaaS for cases, jobs, subjects, GLBA logs, invoices, accounting, and team comms — all in one rigorously fast workspace.</p>
          <div className="grid grid-cols-3 gap-4 pt-6 border-t border-white/10">
            <Stat n="14" l="MODULES" />
            <Stat n="0ms" l="LATENCY" />
            <Stat n="GAAP" l="LEDGER" />
          </div>
        </div>
        <div className="text-xs text-white/40 mono">© 2026 LOGBOOK LEGAL // BUILT FOR PROCESS SERVERS</div>
      </div>
      <div className="flex items-center justify-center p-6 lg:p-12">
        <form onSubmit={submit} className="w-full max-w-md space-y-6" data-testid="auth-form">
          <div className="lg:hidden flex items-center gap-2 mb-4 bg-black p-2 rounded">
            <img src="/logbook-logo.png" alt="LogBook Legal" className="h-8 w-auto" />
          </div>
          <div>
            <div className="label-xs">ENTER</div>
            <h2 className="heading text-3xl mt-1">Sign in</h2>
          </div>
          <Field label="EMAIL" type="email" testid="auth-email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
          <div>
            <label className="label-xs block mb-2">PASSWORD</label>
            <PasswordInput
              data-testid="auth-password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
            />
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer select-none -mt-2" data-testid="remember-me-label">
            <input
              type="checkbox"
              className="h-4 w-4 accent-primary cursor-pointer"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              data-testid="remember-me-checkbox"
            />
            <span className="text-muted-foreground">Remember my email on this device</span>
          </label>
          {idleMsg && <div className="badge badge-default w-full justify-center bg-amber-100 text-amber-800 border-amber-300" data-testid="idle-logout-msg">{idleMsg}</div>}
          {err && <div className="badge badge-red w-full justify-center" data-testid="auth-error">{err}</div>}
          <button type="submit" disabled={loading} className="btn btn-primary w-full justify-center" data-testid="auth-submit-btn">
            {loading ? "..." : "SIGN IN →"}
          </button>
          <div className="relative">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border"></div></div>
            <div className="relative flex justify-center text-xs"><span className="bg-white px-2 text-muted-foreground uppercase tracking-wider">or</span></div>
          </div>
          <button type="button" onClick={google} className="btn btn-secondary w-full justify-center" data-testid="google-signin-btn">
            <GoogleLogo size={16} weight="bold" /> CONTINUE WITH GOOGLE
          </button>
          <Link to="/forgot-password" className="block text-sm text-muted-foreground hover:text-foreground" data-testid="forgot-password-link">
            Forgot password? →
          </Link>
          <div className="text-[11px] text-muted-foreground text-center border-t pt-4" data-testid="invite-only-notice">
            New organizations are invitation-only.<br/>
            Contact your administrator to request access.
          </div>
        </form>
      </div>
    </div>
  );
}

const Stat = ({ n, l }) => (
  <div>
    <div className="heading text-3xl">{n}</div>
    <div className="label-xs text-white/40">{l}</div>
  </div>
);

const Field = ({ label, testid, type = "text", value, onChange }) => (
  <div>
    <label className="label-xs block mb-2">{label}</label>
    <input data-testid={testid} type={type} className="input" value={value} onChange={(e) => onChange(e.target.value)} required />
  </div>
);
